import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";
import { EntryContext, EntryForm, StatCard } from "./AccountingMasterUI";
import { FiPlus, FiRefreshCw } from "react-icons/fi";
import "./AccountingPage.css";
const control =
  "mt-2 min-h-12 w-full rounded-xl border border-gray-200 bg-white p-3 text-sm outline-none focus:border-primary-700";
const button =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary-700 px-5 py-2 font-medium text-white hover:bg-primary-800 disabled:opacity-50";
const empty = () => ({
  purchaseProfileId: "",
  cutoverDate: "",
  reviewNote: "",
  balancesReviewed: false,
  roles: {},
});
const Field = ({ label, children }) => (
  <label className="block text-sm font-medium text-gray-700">
    {label}
    {children}
  </label>
);
export default function SalesSetup() {
  const { accessToken, user } = useAuth(),
    canManage = hasPermission(user, "accounting.masters_manage");
  const [companies, setCompanies] = useState([]),
    [company, setCompany] = useState(""),
    [setup, setSetup] = useState(null),
    [form, setForm] = useState(empty),
    [entry, setEntry] = useState(""),
    [busy, setBusy] = useState(false),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [reload, setReload] = useState(0),
    [history, setHistory] = useState(false);
  useEffect(() => {
    let active = true;
    apiRequest("/accounting/companies", { accessToken })
      .then((r) => {
        if (active) {
          setCompanies(r);
          setCompany(r[0]?.id || "");
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [accessToken]);
  useEffect(() => {
    let active = true;
    setSetup(null);
    setEntry("");
    setForm(empty());
    if (!company) return;
    setLoading(true);
    apiRequest(`/accounting/${company}/sales-setup`, { accessToken })
      .then((r) => {
        if (active) setSetup(r);
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [company, accessToken, reload]);
  const current = setup?.profiles.filter((p) => !p.retiredAt) || [];
  const selected = setup?.purchaseProfiles.find(
    (p) => p.id === form.purchaseProfileId,
  );
  const roles = Object.entries(setup?.roles || {});
  function open(p) {
    setError("");
    setNotice("");
    setForm(
      p
        ? {
            purchaseProfileId:
              setup.purchaseProfiles.find((b) => b.storeId === p.storeId)?.id ||
              "",
            cutoverDate: p.cutoverDate.slice(0, 10),
            reviewNote: p.reviewNote,
            balancesReviewed: false,
            roles: Object.fromEntries(p.roles.map((r) => [r.role, r.ledgerId])),
            expectedProfileId: p.id,
          }
        : empty(),
    );
    setEntry("Sales Posting Configuration");
  }
  async function save(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await apiRequest(`/accounting/${company}/sales-setup`, {
        method: "POST",
        accessToken,
        body: {
          ...form,
          roles: roles.map(([role]) => ({ role, ledgerId: form.roles[role] })),
        },
      });
      setEntry("");
      setNotice(
        "Sales configuration saved. No sale, payment, tax or inventory posting was created.",
      );
      setReload((x) => x + 1);
    } catch (e) {
      setError(e.message || "Unable to save sales setup");
    } finally {
      setBusy(false);
    }
  }
  return (
    <EntryContext.Provider value={{ entry, setEntry, busy, error }}>
      <main className="accounting-master space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500">
              Accounting / Sales Posting Setup
            </p>
            <h1 className="mt-1 text-3xl font-bold text-gray-900">
              Sales Posting Setup
            </h1>
            <p className="mt-2 text-gray-500">
              Review store ownership, sales ledgers and tender clearing before
              connecting POS accounting.
            </p>
          </div>
          {canManage && (
            <button
              className={button}
              disabled={!setup || busy || loading}
              onClick={() => open()}
            >
              <FiPlus />
              Configure Sales
            </button>
          )}
        </header>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
          Configuration only. Sales posting remains disabled. Card and UPI
          clearing are not bank settlements. Saving does not calculate GST, post
          sales, move stock, create customer dues or backfill history.
        </div>
        {error && !entry && (
          <p role="alert" className="rounded-xl bg-red-50 p-4 text-red-800">
            {error}
          </p>
        )}
        {notice && (
          <p
            role="status"
            className="rounded-xl bg-green-50 p-4 text-green-800"
          >
            {notice}
          </p>
        )}
        <section className="flex flex-wrap items-end gap-4 rounded-2xl border border-gray-100 bg-white p-5">
          <div className="w-full max-w-md">
            <Field label="Accounting company">
              <select
                className={control}
                disabled={busy}
                value={company}
                onChange={(e) => {
                  setCompany(e.target.value);
                  setError("");
                  setNotice("");
                }}
              >
                <option value="">Select company</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} · {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <button
            disabled={busy || loading || !company}
            className="inline-flex items-center gap-2 rounded-xl border px-4 py-3 text-sm"
            onClick={() => {
              setError("");
              setReload((x) => x + 1);
            }}
          >
            <FiRefreshCw />
            Refresh setup
          </button>
        </section>
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard label="Configured stores" value={current.length} />
          <StatCard
            label="Need review"
            value={current.filter((p) => !p.configurationValid).length}
          />
          <StatCard label="Sales posting" value="Disabled" />
        </div>
        <section className="rounded-2xl border border-gray-100 bg-white p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-bold">Store sales configurations</h2>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={history}
                onChange={(e) => setHistory(e.target.checked)}
              />
              Show previous versions
            </label>
          </div>
          {loading ? (
            <p>Loading sales setup…</p>
          ) : !setup?.profiles.length ? (
            <p className="py-6 text-sm text-gray-500">
              No sales configurations. Configure store ownership in Posting
              Setup first, then choose the sales ledgers here.
            </p>
          ) : (
            setup.profiles
              .filter((p) => history || !p.retiredAt)
              .map((p) => (
                <article
                  key={p.id}
                  className="rounded-xl border border-gray-200 p-4"
                >
                  <div className="flex flex-wrap justify-between gap-3">
                    <div>
                      <h3 className="font-semibold">
                        {p.purchaseProfile.store.code} ·{" "}
                        {p.purchaseProfile.store.name}
                      </h3>
                      <p className="mt-1 text-xs text-gray-500">
                        GSTIN {p.purchaseProfile.registration.gstin} · Cutover{" "}
                        {p.cutoverDate.slice(0, 10)}
                      </p>
                    </div>
                    <span
                      className={`h-fit rounded-full px-3 py-1 text-xs ${p.retiredAt ? "bg-gray-100 text-gray-600" : p.configurationValid ? "bg-green-50 text-green-800" : "bg-amber-50 text-amber-800"}`}
                    >
                      {p.retiredAt
                        ? "Previous version"
                        : p.configurationValid
                          ? "Configured · posting disabled"
                          : "Needs review"}
                    </span>
                  </div>
                  {p.warnings.map((w) => (
                    <p key={w} className="mt-2 text-xs text-amber-800">
                      {w.replaceAll("_", " ")}
                    </p>
                  ))}
                  <details className="mt-3 text-sm">
                    <summary className="cursor-pointer text-primary-700">
                      Ledger roles and review details
                    </summary>
                    <div className="mt-3 grid gap-2 md:grid-cols-2">
                      {p.roles.map((r) => (
                        <p key={r.role} className="text-xs text-gray-600">
                          {setup.roles[r.role]?.label}: {r.ledger.code} ·{" "}
                          {r.ledger.name}
                        </p>
                      ))}
                    </div>
                    <p className="mt-3 text-sm text-gray-600">{p.reviewNote}</p>
                    <p className="mt-2 break-all text-xs text-gray-400">
                      Created {p.createdAt} · User {p.createdBy}
                    </p>
                  </details>
                  {!p.retiredAt && canManage && (
                    <button
                      className="mt-3 text-sm font-semibold text-primary-700"
                      disabled={busy}
                      onClick={() => open(p)}
                    >
                      Update sales mapping
                    </button>
                  )}
                </article>
              ))
          )}
        </section>
        {canManage && (
          <EntryForm title="Sales Posting Configuration" onSubmit={save}>
            <p className="text-sm leading-6 text-gray-500">
              Nine distinct ledgers are required. Updates save an immutable new
              version. Inventory ownership remains tied to the reviewed store
              purchase mapping; SIS/SOR posting is not enabled.
            </p>
            <Field label="Store / GST ownership mapping">
              <select
                required
                className={control}
                value={form.purchaseProfileId}
                onChange={(e) =>
                  setForm({
                    ...form,
                    purchaseProfileId: e.target.value,
                    balancesReviewed: false,
                  })
                }
              >
                <option value="">Select mapped store</option>
                {setup?.purchaseProfiles
                  .filter(
                    (p) =>
                      !form.expectedProfileId ||
                      p.storeId ===
                        setup.profiles.find(
                          (s) => s.id === form.expectedProfileId,
                        )?.storeId,
                  )
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.store.code} · {p.store.name} · {p.registration.gstin}
                    </option>
                  ))}
              </select>
            </Field>
            {selected && (
              <p className="text-xs text-gray-500">
                Company and GST registration come from this reviewed store
                mapping. Store accounting cutover:{" "}
                {selected.cutoverDate.slice(0, 10)}.
              </p>
            )}
            <Field label="Sales accounting cutover">
              <input
                type="date"
                required
                min={selected?.cutoverDate.slice(0, 10)}
                className={control}
                value={form.cutoverDate}
                onChange={(e) =>
                  setForm({
                    ...form,
                    cutoverDate: e.target.value,
                    balancesReviewed: false,
                  })
                }
              />
            </Field>
            <div className="grid gap-4 md:grid-cols-2">
              {roles.map(([role, spec]) => (
                <Field key={role} label={spec.label}>
                  <select
                    required
                    className={control}
                    value={form.roles[role] || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        balancesReviewed: false,
                        roles: { ...form.roles, [role]: e.target.value },
                      })
                    }
                  >
                    <option value="">
                      Select {spec.kind.toLowerCase()} /{" "}
                      {spec.nature.toLowerCase()}
                    </option>
                    {setup.ledgers
                      .filter((l) => l.allowedRoles.includes(role))
                      .map((l) => (
                        <option
                          key={l.id}
                          value={l.id}
                          disabled={Object.entries(form.roles).some(
                            ([r, id]) => r !== role && id === l.id,
                          )}
                        >
                          {l.code} · {l.name}
                        </option>
                      ))}
                  </select>
                </Field>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              If no suitable ledger is listed, create one in Chart of Accounts.
              Party, return-clearing and incompatible purchase-control ledgers
              are excluded. Rounding may share the purchase rounding ledger.
            </p>
            <Field label="Cutover and prior-balance review note">
              <textarea
                required
                minLength={20}
                maxLength={1000}
                rows={3}
                className={control}
                value={form.reviewNote}
                onChange={(e) =>
                  setForm({
                    ...form,
                    reviewNote: e.target.value,
                    balancesReviewed: false,
                  })
                }
              />
            </Field>
            <label className="flex items-start gap-3 text-sm leading-6 text-gray-700">
              <input
                type="checkbox"
                required
                className="mt-1"
                checked={form.balancesReviewed}
                onChange={(e) =>
                  setForm({ ...form, balancesReviewed: e.target.checked })
                }
              />
              I reviewed prior sales postings, inventory opening balances and
              un-settled tender clearing for this cutover. This does not certify
              GST treatment or authorise historical backfill.
            </label>
            <button
              className={button}
              disabled={
                busy ||
                !selected ||
                !form.balancesReviewed ||
                form.reviewNote.trim().length < 20 ||
                roles.some(([r]) => !form.roles[r])
              }
            >
              Save Sales Configuration
            </button>
          </EntryForm>
        )}
      </main>
    </EntryContext.Provider>
  );
}
